import React, { useEffect, useRef, useState } from 'react';

import AppSpin from '@/components/atoms/AppSpin';
import { useRezeptPdfProgress } from '@/hooks/main/useRezeptPdfProgress';
import { fetchPdfBlob } from '@/services/main/rezept/rezeptListService';
import { getStoredClinicContext } from '@/hooks/main/useClinicContext';
import { hideSsoSyncCurtain } from '@/features/sso/ssoSyncCurtain';

const RezeptPdfProgressPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const [pdfError, setPdfError] = useState<string | null>(null);

  const { status, completedCount, totalCount, isComplete, isFailed, isError, errorMessage } =
    useRezeptPdfProgress(token);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const pdfNavigatedRef = useRef(false);

  // This page has no ProtectedRoute, so no useActiveSessionSync runs to lower the SSO curtain.
  // main.tsx no longer raises it here, but a popup can be served an older cached bundle that
  // still does — take it down on mount so the PDF is never hidden behind it. (HIT-11388)
  useEffect(() => {
    hideSsoSyncCurtain();
  }, []);

  useEffect(() => {
    if (!isComplete || !token || pdfNavigatedRef.current) return;
    pdfNavigatedRef.current = true;

    const showPdf = async () => {
      try {
        const ctx = getStoredClinicContext();
        if (!ctx) {
          setPdfError('治療院情報を取得できませんでした。再度ログインしてから実行してください。');
          return;
        }
        const blob = await fetchPdfBlob(ctx.tenant, ctx.clinicNumber, token);
        // Render the PDF inside an <embed> rather than navigating
        // window.location to the blob URL. Chrome blocks a programmatic
        // top-frame navigation to a blob: URL from this async callback
        // (no user activation), so the PDF never appeared; Firefox allowed it.
        setPdfUrl(URL.createObjectURL(blob));
      } catch (error) {
        setPdfError(await getPdfErrorMessage(error));
        pdfNavigatedRef.current = false;
      }
    };
    showPdf();
  }, [isComplete, token]);

  // Revoke the object URL when it changes or the page unmounts.
  useEffect(() => {
    if (!pdfUrl) return;
    return () => URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  if (!token) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-sm">無効なセッションです</p>
      </div>
    );
  }

  if (pdfUrl) {
    return (
      <embed
        src={pdfUrl}
        type="application/pdf"
        className="w-screen h-screen"
      />
    );
  }

  // isError covers the job row being unreadable (dead job, or token already consumed
  // and purged). Without it the page falls through to the spinner below and shows
  // 印刷処理中 forever, because the hook reports no status at all in that case.
  if (isFailed || isError || pdfError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-1">
        <p className="text-sm text-red-500">エラーが発生しました</p>
        {(pdfError || errorMessage) && (
          <p className="max-w-[80vw] text-center text-xs text-gray-500">
            {pdfError || errorMessage}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen gap-2">
      <AppSpin />
      <p className="text-sm">
        {isComplete ? '表示準備中' : '印刷処理中'}
      </p>
      {status !== 'pending' && (
        <p className="text-xs text-gray-500">
          印刷件数: {completedCount}/{totalCount}件
        </p>
      )}
    </div>
  );
};

async function getPdfErrorMessage(error: unknown): Promise<string> {
  const fallback = 'PDFの取得に失敗しました。しばらくしてから再度実行してください。';
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;

  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      const json = JSON.parse(text) as { message?: string };
      return json.message || fallback;
    } catch {
      return fallback;
    }
  }

  if (responseData && typeof responseData === 'object' && 'message' in responseData) {
    return String((responseData as { message?: unknown }).message || fallback);
  }

  return fallback;
}

export default RezeptPdfProgressPage;
